import React, { useState } from "react"
import {
  Row,
  Col,
  Card,
  CardBody,
  Input,
  Button,
  Table,
  Modal,
  Alert
} from "reactstrap"
import MetaTags from "react-meta-tags"
import Breadcrumbs from "../Common/Breadcrumb"
import Swal from "sweetalert2"
import LoadingSpinner from "components/Reports/EditReports/EditTables/loader"
import jwt from "jsonwebtoken"
import ReactPaginate from "react-paginate"
import {
  useGetTemplatesQuery,
  useGetTemplateByIdQuery,
  useCreateTemplateMutation,
  useUpdateTemplateMutation,
  useDeleteTemplateMutation,
  useGetMessageTemplatesQuery,
  useCreateMessageTemplateMutation,
  useUpdateMessageTemplateMutation,
  useDeleteMessageTemplateMutation,
  useGetLanguagesQuery,
  useGetFormatsQuery,
  useGetTypesQuery,
  useCreateDropdownItemMutation,
  useUpdateDropdownItemMutation,
  useDeleteDropdownItemMutation
} from "../../store/template/template.api"
import _ from "lodash"

// Main component
const EmailTemplateComponent = () => {
  // JWT token decoding for user role
  const decodeToken = token => {
    try {
      const decodedToken = jwt.decode(token)
      return decodedToken
    } catch (error) {
      console.log("Error decoding token:", error)
      return null
    }
  }

  const token = localStorage.getItem("authUser")
  const decodedToken = decodeToken(JSON.parse(token || '""'))
  const role = decodedToken?.role || 0

  // RTK Query hooks
  const { 
    data: templates = [], 
    isLoading: isLoadingTemplates, 
    error: templatesError, 
    refetch: refetchTemplates 
  } = useGetTemplatesQuery()
  
  const [createTemplate, { isLoading: isCreating }] = useCreateTemplateMutation()
  const [updateTemplate, { isLoading: isUpdating }] = useUpdateTemplateMutation()
  const [deleteTemplate, { isLoading: isDeleting }] = useDeleteTemplateMutation()
  
  // Local state
  const [selectedTemplateId, setSelectedTemplateId] = useState(null)
  const { data: selectedTemplate } = useGetTemplateByIdQuery(selectedTemplateId, { 
    skip: !selectedTemplateId 
  })
  
  // Message templates state
  const { data: messageTemplates = [], isLoading: isLoadingMessageTemplates } = 
    useGetMessageTemplatesQuery(selectedTemplateId, { skip: !selectedTemplateId })
  
  const [createMessageTemplate, { isLoading: isCreatingMessage }] = useCreateMessageTemplateMutation()
  const [updateMessageTemplate, { isLoading: isUpdatingMessage }] = useUpdateMessageTemplateMutation()
  const [deleteMessageTemplate, { isLoading: isDeletingMessage }] = useDeleteMessageTemplateMutation()
  
  // Dropdown data queries
  const { data: languages = [] } = useGetLanguagesQuery()
  const { data: formats = [] } = useGetFormatsQuery()
  const { data: types = [] } = useGetTypesQuery()
  
  // Ensure dropdown data is always arrays
  const formattedLanguages = Array.isArray(languages) ? languages : []
  const formattedFormats = Array.isArray(formats) ? formats : []
  const formattedTypes = Array.isArray(types) ? types : []
  
  const [createDropdownItem] = useCreateDropdownItemMutation()
  const [updateDropdownItem] = useUpdateDropdownItemMutation()
  const [deleteDropdownItem] = useDeleteDropdownItemMutation()
  
  // Modals state
  const [modalAddScreen, setModalAddScreen] = useState(false)
  const [modalEditScreen, setModalEditScreen] = useState(false)
  const [modalMessageScreen, setModalMessageScreen] = useState(false)
  const [modalDropdownScreen, setModalDropdownScreen] = useState(false)
  
  // Template state
  const [templateName, setTemplateName] = useState("")
  const [senderValue, setSenderValue] = useState("")
  const [editTemplateValue, setEditTemplate] = useState("")
  const [editSenderValue, setEditSenderValue] = useState("")
  
  // Message template state
  const [selectedMessageTemplateId, setSelectedMessageTemplateId] = useState(null)
  const [messageText, setMessageText] = useState("")
  const [languageId, setLanguageId] = useState("")
  const [formatId, setFormatId] = useState("")
  const [typeId, setTypeId] = useState("")
  const [codeLength, setCodeLength] = useState("")
  const [isEditingMessage, setIsEditingMessage] = useState(false)
  
  // Dropdown management state
  const [dropdownCategory, setDropdownCategory] = useState("languages")
  const [dropdownItemValue, setDropdownItemValue] = useState("")
  const [selectedDropdownItemId, setSelectedDropdownItemId] = useState(null)
  const [isEditingDropdown, setIsEditingDropdown] = useState(false)
  
  // Pagination and search
  const [currentPage, setCurrentPage] = useState(0)
  const [searchTerm, setSearchTerm] = useState("")
  const [searchText, setSearchText] = useState("")
  const [error, setError] = useState(null)
  
  // Pagination logic
  const itemsPerPage = 10
  const offset = currentPage * itemsPerPage
  
  // Filter templates based on search
  const filteredTemplates = _.get(templates, 'templates', []).filter(item => {
    if (!searchTerm) return true
    return item.label?.toLowerCase().includes(searchText.toLowerCase())
  })
  
  const currentPageItems = filteredTemplates.slice(offset, offset + itemsPerPage)
  
  // Toast notification setup
  const Toast = Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 1500,
    timerProgressBar: true,
    didOpen: toast => {
      toast.addEventListener("mouseenter", Swal.stopTimer)
      toast.addEventListener("mouseleave", Swal.resumeTimer)
    },
  })

  // Modal toggle functions
  const toggleAddScreen = () => {
    setModalAddScreen(!modalAddScreen)
    setError(null)
  }
  
  const toggleEditScreen = () => {
    setModalEditScreen(!modalEditScreen)
    setError(null)
  }
  
  const toggleMessageScreen = () => {
    if (!modalMessageScreen) {
      // Reset form when opening
      setSelectedMessageTemplateId(null)
      setMessageText("")
      setLanguageId("")
      setFormatId("")
      setTypeId("")
      setCodeLength("")
      setIsEditingMessage(false)
    }
    setModalMessageScreen(!modalMessageScreen)
    setError(null)
  }
  
  const toggleDropdownScreen = () => {
    if (!modalDropdownScreen) {
      // Reset form when opening
      setDropdownItemValue("")
      setSelectedDropdownItemId(null)
      setIsEditingDropdown(false)
    }
    setModalDropdownScreen(!modalDropdownScreen)
    setError(null)
  }
  
  // Open edit modal with template data
  const openEditModal = (id) => {
    setSelectedTemplateId(id)
    setModalEditScreen(true)
    // Find template by id and set initial value
    const foundTemplate = _.get(templates, 'templates', []).find(t => t.id === id)
    if (foundTemplate) {
      setEditTemplate(foundTemplate.label)
      setEditSenderValue(foundTemplate.sender || "")
    }
  }
  
  // Open message template edit modal
  const openMessageModal = (id) => {
    const messageTemplate = messageTemplates.find(mt => mt.id === id)
    if (messageTemplate) {
      setSelectedMessageTemplateId(id)
      setMessageText(messageTemplate.messageText)
      setLanguageId(messageTemplate.languageId)
      setFormatId(messageTemplate.formatId)
      setTypeId(messageTemplate.typeId)
      setCodeLength(messageTemplate.codeLength)
      setIsEditingMessage(true)
      setModalMessageScreen(true)
    }
  }
  
  // Open dropdown item edit modal
  const openDropdownEditModal = (id, value, category) => {
    setSelectedDropdownItemId(id)
    setDropdownItemValue(value)
    setDropdownCategory(category)
    setIsEditingDropdown(true)
    setModalDropdownScreen(true)
  }
  
  // Create new template
  const handleCreateTemplate = async () => {
    try {
      setError(null)
      // Validation
      if (!templateName.trim()) {
        setError("Template name is required")
        return
      }
      // Check for duplicates
      const exists = _.get(templates, 'templates', []).some(
        item => item.label.toLowerCase() === templateName.toLowerCase()
      )
      if (exists) {
        setError("Template name already exists")
        return
      }
      await createTemplate({ template_name: templateName, sender: senderValue }).unwrap()
      Toast.fire({
        icon: "success",
        title: "Template created successfully!"
      })
      setModalAddScreen(false)
      setTemplateName("")
      setSenderValue("")
      refetchTemplates()
    } catch (err) {
      console.error("Failed to create template:", err)
      setError("Failed to create template. Please try again.")
    }
  }
  
  // Update existing template
  const handleUpdateTemplate = async () => {
    try {
      setError(null)
      // Validation
      if (!editTemplateValue?.trim()) {
        setError("Template name is required")
        return
      }
      // Check for duplicates (excluding the current template)
      const exists = _.get(templates, 'templates', []).some(
        item => item.label.toLowerCase() === editTemplateValue.toLowerCase() 
          && item.id !== selectedTemplateId
      )
      if (exists) {
        setError("Template name already exists")
        return
      }
      await updateTemplate({
        id: selectedTemplateId,
        template_name: editTemplateValue,
        sender: editSenderValue
      }).unwrap()
      Toast.fire({
        icon: "success",
        title: "Template updated successfully!"
      })
      setModalEditScreen(false)
      setEditTemplate("")
      setEditSenderValue("")
      refetchTemplates()
    } catch (err) {
      console.error("Failed to update template:", err)
      setError("Failed to update template. Please try again.")
    }
  }
  
  // Delete template
  const handleDeleteTemplate = async (id) => {
    try {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete it!",
        cancelButtonText: "No, cancel!",
        reverseButtons: true
      })
      
      if (result.isConfirmed) {
        await deleteTemplate(id).unwrap()
        
        Toast.fire({
          icon: "success",
          title: "Template deleted successfully!"
        })
        
        refetchTemplates()
        setCurrentPage(0)
      }
    } catch (err) {
      console.error("Failed to delete template:", err)
      
      // Show a more detailed error for template with associations
      Toast.fire({
        icon: "error",
        title: "Deletion failed!",
        text: "Please check for batches that are associated with this template.",
        timer: 3000
      })
    }
  }
  
  // Handle message template operations
  const handleSaveMessageTemplate = async () => {
    try {
      setError(null)
      
      // Validation
      if (!messageText.trim()) {
        setError("Message text is required")
        return
      }
      
      if (!languageId) {
        setError("Language is required")
        return
      }
      
      if (!formatId) {
        setError("Format is required")
        return
      }
      
      if (!typeId) {
        setError("Type is required")
        return
      }
      
      const messageTemplateData = {
        templateId: selectedTemplateId,
        messageText,
        formatId,
        languageId,
        typeId,
        codeLength: codeLength || "0"
      }
      
      if (isEditingMessage) {
        await updateMessageTemplate({
          id: selectedMessageTemplateId,
          ...messageTemplateData
        }).unwrap()
        
        Toast.fire({
          icon: "success",
          title: "Message template updated successfully!"
        })
        
        // Reset form fields but keep modal open
        setMessageText("")
        setLanguageId("")
        setFormatId("")
        setTypeId("")
        setCodeLength("")
        setSelectedMessageTemplateId(null)
        setIsEditingMessage(false)
      } else {
        await createMessageTemplate(messageTemplateData).unwrap()
        
        Toast.fire({
          icon: "success",
          title: "Message template created successfully!"
        })
        
        // Reset form fields but keep modal open
        setMessageText("")
        setLanguageId("")
        setFormatId("")
        setTypeId("")
        setCodeLength("")
      }
    } catch (err) {
      console.error("Failed to save message template:", err)
      setError("Failed to save message template. Please try again.")
    }
  }
  
  const handleDeleteMessageTemplate = async (id) => {
    try {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete it!",
        cancelButtonText: "No, cancel!",
        reverseButtons: true
      })
      
      if (result.isConfirmed) {
        await deleteMessageTemplate(id).unwrap()
        
            Toast.fire({
              icon: "success",
          title: "Message template deleted successfully!"
        })
      }
    } catch (err) {
      console.error("Failed to delete message template:", err)
      
            Toast.fire({
              icon: "error",
        title: "Deletion failed!",
        timer: 3000
      })
    }
  }
  
  // Handle dropdown item operations
  const handleSaveDropdownItem = async () => {
    try {
      setError(null)
      
      // Validation
      if (!dropdownItemValue.trim()) {
        setError("Value is required")
        return
      }
      
      if (isEditingDropdown) {
        await updateDropdownItem({
          category: dropdownCategory,
          id: selectedDropdownItemId,
          value: dropdownItemValue
        }).unwrap()
        
        Toast.fire({
          icon: "success",
          title: "Item updated successfully!"
        })
        
        // Reset form fields but keep modal open
        setDropdownItemValue("")
        setSelectedDropdownItemId(null)
        setIsEditingDropdown(false)
      } else {
        await createDropdownItem({
          category: dropdownCategory,
          value: dropdownItemValue
        }).unwrap()
        
        Toast.fire({
          icon: "success",
          title: "Item created successfully!"
        })
        
        // Reset form fields but keep modal open
        setDropdownItemValue("")
      }
    } catch (err) {
      console.error("Failed to save dropdown item:", err)
      setError("Failed to save item. Please try again.")
    }
  }
  
  const handleDeleteDropdownItem = async (id, category) => {
    try {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete it!",
        cancelButtonText: "No, cancel!",
        reverseButtons: true
      })
      
      if (result.isConfirmed) {
        await deleteDropdownItem({
          category,
          id
        }).unwrap()
        
        Toast.fire({
          icon: "success",
          title: "Item deleted successfully!"
        })
      }
    } catch (err) {
      console.error("Failed to delete dropdown item:", err)
      
      Toast.fire({
        icon: "error",
        title: "Deletion failed!",
        text: "This item may be in use by message templates.",
        timer: 3000
      })
    }
  }

  return (
    <React.Fragment>
      <div>
        <MetaTags>
          <title>Templates</title>
        </MetaTags>
        <div className="container-fluid">
          <Breadcrumbs title="Settings" breadcrumbItem="Templates" />

          <Row>
            <Col className="col-12">
              <Card>
                <CardBody>
                <Row className="mb-2">
                  <Col md="8" sm="12">
                    <div className="search-box me-2 mb-2 d-inline-block w-sm-100">
                      <Row>
                        <Col>
                          <div
                            className="me-3"
                            style={{
                              border: "1px solid #D9D9D9",
                              borderRadius: "50px",
                              marginBottom: "10px",
                              minWidth: "210px",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <i
                                className="bx bx-search-alt search-icon sm-left-30 ps-2 pe-2 "
                              style={{
                                width: "10px",
                                height: "10px",
                              }}
                            ></i>
                            <input
                              type="text"
                              placeholder="Search"
                              style={{
                                width: "150px",
                                height: "31px",
                                border: "none",
                                borderRadius: "10px",
                                textAlign: "start",
                                borderTopLeftRadius: "20px",
                                borderBottomLeftRadius: "20px",
                                marginTop: "2px",
                                marginLeft:"25px"
                              }}
                              value={searchTerm}
                              onChange={e => {
                                setSearchTerm(e.target.value)
                                setSearchText(e.target.value)
                                setCurrentPage(0) // Reset to first page when searching
                              }}
                            />
                            {searchTerm !== "" && (
                              <i
                                className="fas fa-times me-2 ms-2"
                                style={{
                                  cursor: "pointer",
                                }}
                                onClick={() => {
                                  setSearchTerm("")
                                  setSearchText("")
                                  setCurrentPage(0) // Reset to first page when clearing search
                                }}
                              ></i>
                            )}
                          </div>
                        </Col>
                      </Row>
                    </div>
                  </Col>

                    <Col md="4" sm="12" className="position-relative">
                      <div className="d-flex justify-content-end">
                      <Button
                        color="danger"
                          className="btn btn-custom me-2"
                          style={{ 
                            height: "38px", 
                            padding: "0.375rem 0.75rem", 
                            lineHeight: "1.5",
                            width: "auto",
                            minWidth: "fit-content"
                          }}
                          onClick={toggleAddScreen}
                          disabled={role === 4 || isCreating}
                        >
                          {isCreating ? "Adding..." : "Add Template"}
                        </Button>
                        <Button
                          color="danger"
                          className="btn btn-custom"
                          style={{ 
                            height: "38px", 
                            padding: "0.375rem 0.75rem", 
                            lineHeight: "1.5", 
                            whiteSpace: "nowrap",
                            width: "auto",
                            minWidth: "fit-content"
                          }}
                          onClick={toggleDropdownScreen}
                        disabled={role === 4}
                      >
                          Manage Dropdown Data
                      </Button>
                    </div>
                  </Col>
                </Row>
                  
                <div className="col-12 ms-3">
                    {isLoadingTemplates ? (
                      <div className="text-center">
                        <LoadingSpinner />
                      </div>
                    ) : templatesError ? (
                      <Alert color="danger">
                        Error loading templates. Please try again.
                      </Alert>
                    ) : filteredTemplates.length === 0 ? (
                        <div
                          style={{
                            textAlign: "center",
                            paddingTop: "50px",
                          }}
                        >
                        {searchTerm ? "No matching templates found" : "No templates found"}
                        </div>
                    ) : (
                      <div>
                        <Row style={{ marginRight: "15px" }}>
                      <Table className="table-responsive align-middle mb-0 report_table_edit">
                      <thead>
                        <tr>
                          <th>Template ID</th>
                          <th>Template </th>
                          <th>Sender</th>
                          <th>Message Templates</th>
                          <th>Action</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                              {currentPageItems.map(template => (
                                <tr key={template.id}>
                                  <td>{template.id}</td>
                                  <td>{template.label}</td>
                                  <td>{template.sender || '-'}</td>
                              <td>           
                              <button
                              className={`view-btn-tbl ${role === 4 ? "disabled" : ""}`}
                                      onClick={() => {
                                        setSelectedTemplateId(template.id)
                                        toggleMessageScreen()
                              }}
                              disabled={role === 4}
                            >
                                      Manage Messages
                            </button>
                              </td>
                              <td>
                              <button
                                className={`view-btn-tbl ${role === 4 ? "disabled" : ""}`}
                                      onClick={() => openEditModal(template.id)}
                                      disabled={role === 4 || isUpdating}
                                    >
                                      {isUpdating && selectedTemplateId === template.id 
                                        ? "Editing..." 
                                        : "Edit"
                                      }
                                    </button>
                                  </td>
                                  <td>
                                    <button
                                      className={`view-btn-tbl ${role === 4 ? "disabled" : ""}`}
                                      onClick={() => handleDeleteTemplate(template.id)}
                                      disabled={role === 4 || isDeleting}
                                    >
                                      {isDeleting && selectedTemplateId === template.id 
                                        ? "Deleting..." 
                                        : "Delete"
                                      }
                              </button>
                              </td>
                            </tr>
                              ))}
                      </tbody>
                    </Table>
                  </Row>
                      </div>
                    )}
                  </div>
                  
                  {filteredTemplates.length > 0 && (
                  <div className="col-12">
                    <ReactPaginate
                      previousLabel={"previous"}
                      nextLabel={"next"}
                      breakLabel={"..."}
                        pageCount={Math.ceil(filteredTemplates.length / itemsPerPage)}
                      marginPagesDisplayed={2}
                      forcePage={currentPage}
                      pageRangeDisplayed={5}
                      onPageChange={data => {
                        setCurrentPage(data.selected)
                      }}
                      containerClassName={"pagination"}
                      activeClassName={"active"}
                    />
                  </div>
                  )}

                  {/* Add Template Modal */}
                  <Modal
                    size="md"
                    isOpen={modalAddScreen}
                    toggle={toggleAddScreen}
                    className="modal-popscreen"
                  >
                    <div className="modal-header">
                      <h5
                        className="modal-title mt-0"
                        id="exampleModalFullscreenLabel"
                      >
                        Add Template
                      </h5>
                      <button
                        onClick={toggleAddScreen}
                        type="button"
                        className="close"
                        data-dismiss="modal"
                        aria-label="Close"
                      >
                        <span aria-hidden="true">&times;</span>
                      </button>
                    </div>
                    <div className="modal-body">
                      {error && (
                        <Alert color="danger" className="mb-3">
                          {error}
                        </Alert>
                      )}
                      <form>
                        <div className="customer-field-container">
                          <Row>
                            <Col md="4">
                              <label className="lable-customer">
                                Template Name
                              </label>
                            </Col>
                            <Col md="8">
                              <Input
                                type="text"
                                className="customer-input"
                                placeholder="Enter Template Name"
                                value={templateName}
                                onChange={e => setTemplateName(e.target.value)}
                              />
                            </Col>
                          </Row>
                          <Row className="mt-3">
                            <Col md="4">
                              <label className="lable-customer">
                                Sender 
                              </label>
                            </Col>
                            <Col md="8">
                              <Input
                                type="text"
                                className="customer-input"
                                placeholder="Enter Sender"
                                value={senderValue}
                                onChange={e => setSenderValue(e.target.value)}
                              />
                            </Col>
                          </Row>
                        </div>
                      </form>
                    </div>

                    <div className="modal-footer">
                      <button
                        type="button"
                        onClick={toggleAddScreen}
                        className="btn btn-secondary"
                        data-dismiss="modal"
                        disabled={isCreating}
                      >
                        Close
                      </button>
                      <Button
                        type="button"
                        className="btn btn-primary"
                        color="danger"
                        onClick={handleCreateTemplate}
                        disabled={isCreating}
                      >
                        {isCreating ? "Saving..." : "Save"}
                      </Button>
                    </div>
                  </Modal>

                  {/* Edit Template Modal */}
                  <Modal
                    size="md"
                    isOpen={modalEditScreen}
                    toggle={toggleEditScreen}
                    className="modal-popscreen"
                  >
                    <div className="modal-header">
                      <h5
                        className="modal-title mt-0"
                        id="exampleModalFullscreenLabel"
                      >
                        Edit Template
                      </h5>
                      <button
                        onClick={toggleEditScreen}
                        type="button"
                        className="close"
                        data-dismiss="modal"
                        aria-label="Close"
                      >
                        <span aria-hidden="true">&times;</span>
                      </button>
                    </div>
                    <div className="modal-body">
                      {error && (
                        <Alert color="danger" className="mb-3">
                          {error}
                        </Alert>
                      )}
                      <form>
                        <div className="customer-field-container">
                          <Row>
                            <Col md="4">
                              <label className="lable-customer">
                                Template Name
                              </label>
                            </Col>
                            <Col md="8">
                              <Input
                                type="text"
                                className="customer-input"
                                value={editTemplateValue}
                                placeholder="Enter Template Name"
                                onChange={e => setEditTemplate(e.target.value)}
                              />
                            </Col>
                          </Row>
                          <Row className="mt-3">
                            <Col md="4">
                              <label className="lable-customer">
                                Sender
                              </label>
                            </Col>
                            <Col md="8">
                              <Input
                                type="text"
                                className="customer-input"
                                value={editSenderValue}
                                placeholder="Enter Sender Name"
                                onChange={e => setEditSenderValue(e.target.value)}
                              />
                            </Col>
                          </Row>
                        </div>
                      </form>
                    </div>

                    <div className="modal-footer">
                      <button
                        type="button"
                        onClick={toggleEditScreen}
                        className="btn btn-secondary"
                        data-dismiss="modal"
                        disabled={isUpdating}
                      >
                        Close
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary btn-danger"
                        onClick={handleUpdateTemplate}
                        disabled={isUpdating}
                      >
                        {isUpdating ? "Saving..." : "Save changes"}
                      </button>
                    </div>
                  </Modal>
                  
                  {/* Message Templates Modal */}
                  <Modal
                    size="lg"
                    isOpen={modalMessageScreen}
                    toggle={toggleMessageScreen}
                    className="modal-popscreen"
                  >
                    <div className="modal-header">
                      <h5
                        className="modal-title mt-0"
                        id="exampleModalFullscreenLabel"
                      >
                        {isEditingMessage ? "Edit Message Template" : "Add Message Template"}
                      </h5>
                      <button
                        onClick={toggleMessageScreen}
                        type="button"
                        className="close"
                        data-dismiss="modal"
                        aria-label="Close"
                      >
                        <span aria-hidden="true">&times;</span>
                      </button>
                    </div>
                    <div className="modal-body">
                      {error && (
                        <Alert color="danger" className="mb-3">
                          {error}
                        </Alert>
                      )}
                      
                      {/* Message Template Form */}
                      <div className="mb-4">
                        <Row className="mb-3">
                          <Col md="3">
                            <label className="form-label">Message Text</label>
                            <small className="text-muted d-block">
                              Use {"{TEST_ID_TEXT}"} as a placeholder for dynamic content.
                            </small>
                          </Col>
                          <Col md="9">
                            <Input
                              type="textarea"
                              rows="3"
                              className="form-control"
                              placeholder="Enter message text"
                              value={messageText}
                              onChange={e => setMessageText(e.target.value)}
                            />
                          </Col>
                        </Row>
                        
                        <Row className="mb-3">
                          <Col md="3">
                            <label className="form-label">Language</label>
                          </Col>
                          <Col md="9">
                            <Input
                              type="select"
                              className="form-control"
                              value={languageId}
                              onChange={e => setLanguageId(e.target.value)}
                            >
                              <option value="">Select Language</option>
                              {formattedLanguages.map(lang => (
                                <option key={lang.id} value={lang.id}>
                                  {lang.value}
                                </option>
                              ))}
                            </Input>
                          </Col>
                        </Row>
                        <Row className="mb-3">
                          <Col md="3">
                            <label className="form-label">Format</label>
                          </Col>
                          <Col md="9">
                            <Input
                              type="select"
                              className="form-control"
                              value={formatId}
                              onChange={e => setFormatId(e.target.value)}
                            >
                              <option value="">Select Format</option>
                              {formattedFormats.map(format => (
                                <option key={format.id} value={format.id}>
                                  {format.value}
                                </option>
                              ))}
                            </Input>
                          </Col>
                        </Row>
                        <Row className="mb-3">
                          <Col md="3">
                            <label className="form-label">Type</label>
                          </Col>
                          <Col md="9">
                            <Input
                              type="select"
                              className="form-control"
                              value={typeId}
                              onChange={e => setTypeId(e.target.value)}
                            >
                              <option value="">Select Type</option>
                              {formattedTypes.map(type => (
                                <option key={type.id} value={type.id}>
                                  {type.value}
                                </option>
                              ))}
                            </Input>
                          </Col>
                        </Row>
                        <Row className="mb-3">
                          <Col md="3">
                            <label className="form-label">Code Length</label>
                          </Col>
                          <Col md="9">
                            <Input
                              type="number"
                              className="form-control"
                              placeholder="Enter code length"
                              value={codeLength}
                              onChange={e => setCodeLength(e.target.value)}
                            />
                          </Col>
                        </Row>
                        <Row>
                          <Col className="text-end">
                            <Button
                              color="danger"
                              onClick={handleSaveMessageTemplate}
                              disabled={isCreatingMessage || isUpdatingMessage}
                            >
                              {isCreatingMessage || isUpdatingMessage ? "Saving..." : "Save Message Template"}
                            </Button>
                          </Col>
                        </Row>
                      </div>
                      
                      {/* Message Templates List */}
                      <h5 className="mt-4 mb-3">Message Templates</h5>
                      {isLoadingMessageTemplates ? (
                        <div className="text-center">
                          <LoadingSpinner />
                        </div>
                      ) : messageTemplates.length === 0 ? (
                        <div className="text-center py-3">
                          <p>No message templates found</p>
                        </div>
                      ) : (
                        <Table responsive className="table-centered table-nowrap">
                          <thead>
                            <tr>
                              <th>ID</th>
                              <th>Message Text</th>
                              <th>Language</th>
                              <th>Format</th>
                              <th>Type</th>
                              <th>Code Length</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {messageTemplates.map(item => {
                              const language = formattedLanguages.find(l => l.id === item.languageId);
                              const format = formattedFormats.find(f => f.id === item.formatId);
                              const type = formattedTypes.find(t => t.id === item.typeId);
                              
                              return (
                                <tr key={item.id}>
                                  <td>{item.id}</td>
                                  <td>
                                    {item.messageText.length > 30
                                      ? `${item.messageText.substring(0, 30)}...`
                                      : item.messageText}
                                  </td>
                                  <td>{language?.value || '-'}</td>
                                  <td>{format?.value || '-'}</td>
                                  <td>{type?.value || '-'}</td>
                                  <td>{item.codeLength || '0'}</td>
                                  <td>
                                    <button
                                      className="btn btn-sm btn-info me-2"
                                      onClick={() => openMessageModal(item.id)}
                                      disabled={isUpdatingMessage || isDeletingMessage}
                                    >
                                      Edit
                                    </button>
                                    <button
                                      className="btn btn-sm btn-danger"
                                      onClick={() => handleDeleteMessageTemplate(item.id)}
                                      disabled={isUpdatingMessage || isDeletingMessage}
                                    >
                                      Delete
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      )}
                    </div>
                    <div className="modal-footer">
                      <button
                        type="button"
                        onClick={toggleMessageScreen}
                        className="btn btn-secondary"
                        data-dismiss="modal"
                      >
                        Close
                      </button>
                    </div>
                  </Modal>
                  
                  {/* Dropdown Management Modal */}
                  <Modal
                    size="lg"
                    isOpen={modalDropdownScreen}
                    toggle={toggleDropdownScreen}
                    className="modal-popscreen"
                  >
                    <div className="modal-header">
                      <h5
                        className="modal-title mt-0"
                        id="exampleModalFullscreenLabel"
                      >
                        Manage Dropdown Data
                      </h5>
                      <button
                        onClick={toggleDropdownScreen}
                        type="button"
                        className="close"
                        data-dismiss="modal"
                        aria-label="Close"
                      >
                        <span aria-hidden="true">&times;</span>
                      </button>
                    </div>
                    <div className="modal-body">
                      {error && (
                        <Alert color="danger" className="mb-3">
                          {error}
                        </Alert>
                      )}
                      
                      {/* Category selection */}
                      <Row className="mb-3">
                        <Col md="3">
                          <label className="form-label">Category</label>
                        </Col>
                        <Col md="9">
                          <Input
                            type="select"
                            className="form-control"
                            value={dropdownCategory}
                            onChange={e => setDropdownCategory(e.target.value)}
                          >
                            <option value="languages">Languages</option>
                            <option value="formats">Formats</option>
                            <option value="types">Types</option>
                          </Input>
                        </Col>
                      </Row>
                      
                      {/* Add/Edit form */}
                      <Row className="mb-3">
                        <Col md="3">
                          <label className="form-label">Value</label>
                        </Col>
                        <Col md="7">
                          <Input
                            type="text"
                            className="form-control"
                            placeholder="Enter value"
                            value={dropdownItemValue}
                            onChange={e => setDropdownItemValue(e.target.value)}
                          />
                        </Col>
                        <Col md="2">
                          <Button
                            color="danger"
                            className="w-100"
                            onClick={handleSaveDropdownItem}
                          >
                            {isEditingDropdown ? "Update" : "Add"}
                          </Button>
                        </Col>
                      </Row>
                      
                      {/* Display dropdown items */}
                      <div className="mt-4">
                        <h5 className="mb-3">
                          {dropdownCategory === "languages" ? "Languages" : 
                           dropdownCategory === "formats" ? "Formats" : "Types"}
                        </h5>
                        
                        <Table responsive className="table-centered table-nowrap">
                          <thead>
                            <tr>
                              <th>ID</th>
                              <th>Value</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(dropdownCategory === "languages" ? formattedLanguages :
                              dropdownCategory === "formats" ? formattedFormats : formattedTypes
                            ).map(item => (
                              <tr key={item.id}>
                                <td>{item.id}</td>
                                <td>{item.value}</td>
                                <td>
                                  <button
                                    className="btn btn-sm btn-info me-2"
                                    onClick={() => openDropdownEditModal(item.id, item.value, dropdownCategory)}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    className="btn btn-sm btn-danger"
                                    onClick={() => handleDeleteDropdownItem(item.id, dropdownCategory)}
                                  >
                                    Delete
                                  </button>
                                </td>
                              </tr>
                            ))}
                            {(dropdownCategory === "languages" ? formattedLanguages.length === 0 :
                              dropdownCategory === "formats" ? formattedFormats.length === 0 : formattedTypes.length === 0
                            ) && (
                              <tr>
                                <td colSpan="3" className="text-center">
                                  No items found
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </Table>
                      </div>
                    </div>
                    <div className="modal-footer">
                      <button
                        type="button"
                        onClick={toggleDropdownScreen}
                        className="btn btn-secondary"
                        data-dismiss="modal"
                      >
                        Close
                      </button>
                    </div>
                  </Modal>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </React.Fragment>
  )
}

export default EmailTemplateComponent
